import React, { useState, useEffect, useMemo } from 'react'
import {
  X, Edit, Trash2, AlertTriangle, CheckCircle, XCircle,
  Mail, Hash, Calendar, CheckSquare, Type, LinkIcon, Phone,
  Image, Video, Music, File, Upload, Loader2
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { dataInputsService } from '@/services/dataInputs'
import { apiRequest } from '@/services/api'
import NumberInput from '@/components/form/NumberInput'
import toast from 'react-hot-toast'

const BulkOperationsModal = ({ 
  isOpen, 
  onClose, 
  selectedRecords, 
  dataInput, 
  onSuccess 
}) => {
  const [operation, setOperation] = useState('update') // 'update' or 'delete'
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [operationResult, setOperationResult] = useState(null)
  const [fileUploads, setFileUploads] = useState({}) // Track file uploads by attribute name
  const [uploadingFiles, setUploadingFiles] = useState({}) // Track upload progress
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors }
  } = useForm()

  // Get all active attributes for display, sorted by display_order
  const activeAttributes = useMemo(() => {
    const filtered = dataInput?.attributes?.filter(attr => attr.is_active) || []
    return filtered.sort((a, b) => {
      // Sort by display_order first, then by id as fallback
      if (a.display_order !== b.display_order) {
        return a.display_order - b.display_order
      }
      return a.id - b.id
    })
  }, [dataInput?.attributes])

  // Get non-unique attributes for bulk update (only these can be edited)
  const nonUniqueAttributes = activeAttributes.filter(attr => !attr.is_unique)

  // File upload handler
  const handleFileUpload = async (file, attributeName) => {
    setUploadingFiles(prev => ({ ...prev, [attributeName]: true }))

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('is_public', 'true')
      formData.append('generate_thumbnail', 'true')

      const response = await apiRequest.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const uniqueFilename = response.data.file.filename
      setFileUploads(prev => ({ ...prev, [attributeName]: response.data.file }))
      setValue(attributeName, uniqueFilename)  // Store unique filename instead of ID
      toast.success(`${file.name} uploaded successfully`)

    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    } finally {
      setUploadingFiles(prev => ({ ...prev, [attributeName]: false }))
    }
  }

  // Get field icon based on data type
  const getFieldIcon = (dataType) => {
    switch (dataType) {
      case 'Email': return Mail
      case 'URL': return LinkIcon
      case 'Phone Number': return Phone
      case 'Number':
      case 'Decimal': return Hash
      case 'Date':
      case 'Datetime': return Calendar
      case 'Boolean': return CheckSquare
      case 'Image': return Image
      case 'Video': return Video
      case 'Audio': return Music
      case 'File': return File
      case 'Document': return File
      case 'Archive': return File
      default: return Type
    }
  }

  // Get file accept types for file inputs
  const getFileAcceptTypes = (dataType) => {
    switch (dataType) {
      case 'Image': return 'image/*'
      case 'Video': return 'video/*'
      case 'Audio': return 'audio/*'
      case 'Document': return '.pdf,.doc,.docx,.txt,.rtf'
      case 'Archive': return '.zip,.rar,.7z,.tar,.gz'
      case 'File':
      default: return '*/*'
    }
  }

  // Render field based on data type with proper validation
  const renderField = (attribute) => {
    const Icon = getFieldIcon(attribute.data_type)
    const fieldName = attribute.name

    // No required validation for bulk updates - only update filled fields
    const baseValidation = {}

    // If attribute has allowed_values, show dropdown
    if (attribute.allowed_values && attribute.allowed_values.length > 0) {
      return (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-gray-400" />
          </div>
          <select
            {...register(fieldName, baseValidation)}
            className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
              errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
            }`}
          >
            <option value="">Leave unchanged...</option>
            {attribute.allowed_values.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )
    }

    // Otherwise, show input based on data type
    switch (attribute.data_type) {
      case 'Email':
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register(fieldName, {
                ...baseValidation,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              type="email"
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
              }`}
              placeholder={attribute.placeholder || `Enter ${attribute.label || attribute.name}`}
            />
          </div>
        )

      case 'URL':
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register(fieldName, {
                ...baseValidation,
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'Invalid URL format'
                }
              })}
              type="url"
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
              }`}
              placeholder={attribute.placeholder || 'https://example.com'}
            />
          </div>
        )

      case 'Number':
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <NumberInput
              {...register(fieldName, {
                ...baseValidation,
                validate: (value) => {
                  if (!value) return true
                  const num = parseInt(value)
                  return !isNaN(num) || 'Must be a valid number'
                }
              })}
              allowDecimals={false}
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
              }`}
              placeholder={attribute.placeholder || 'Enter number (e.g., 1,234)'}
            />
          </div>
        )

      case 'Decimal':
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <NumberInput
              {...register(fieldName, {
                ...baseValidation,
                validate: (value) => {
                  if (!value) return true
                  const cleanValue = value.replace(/,/g, '')
                  const num = parseFloat(cleanValue)
                  return !isNaN(num) || 'Must be a valid decimal number'
                }
              })}
              allowDecimals={true}
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
              }`}
              placeholder={attribute.placeholder || 'Enter decimal (e.g., 1,234.56)'}
            />
          </div>
        )

      case 'Date':
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register(fieldName, baseValidation)}
              type="date"
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
              }`}
            />
          </div>
        )

      case 'Datetime':
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register(fieldName, baseValidation)}
              type="datetime-local"
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
              }`}
            />
          </div>
        )

      case 'Phone Number':
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register(fieldName, {
                pattern: {
                  value: /^0\d{7,14}$/,
                  message: 'Phone number must start with 0 and contain 8-15 digits'
                }
              })}
              type="tel"
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
              }`}
              placeholder="0123456789 (leave empty to skip)"
            />
          </div>
        )

      case 'Boolean':
        return (
          <div className="flex items-center pl-10">
            <input
              {...register(fieldName)}
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-700">
              Check to set as true (leave unchecked to leave unchanged)
            </label>
          </div>
        )

      case 'File':
      case 'Image':
      case 'Video':
      case 'Audio':
      case 'Document':
      case 'Archive':
        return (
          <div className="space-y-2">
            {/* Current file display */}
            {fileUploads[fieldName] && (
              <div className="flex items-center space-x-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
                <Icon className="h-4 w-4" />
                <span>Selected: {fileUploads[fieldName].original_filename}</span>
              </div>
            )}

            {/* File upload input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {uploadingFiles[fieldName] ? (
                  <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                ) : (
                  <Icon className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <input
                type="file"
                accept={getFileAcceptTypes(attribute.data_type)}
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    handleFileUpload(file, fieldName)
                  }
                }}
                disabled={uploadingFiles[fieldName]}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {uploadingFiles[fieldName] && (
              <div className="text-xs text-blue-600">
                Uploading file...
              </div>
            )}

            <div className="text-xs text-gray-500">
              Upload a new file to replace existing files in selected records
            </div>
          </div>
        )

      default: // Text and other types
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register(fieldName, baseValidation)}
              type="text"
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
              }`}
              placeholder={attribute.placeholder || `Enter ${attribute.label || attribute.name}`}
            />
          </div>
        )
    }
  }

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async (data) => {
      const updates = {}

      // Build updates object from form data
      nonUniqueAttributes.forEach(attr => {
        const value = data[attr.name]

        // Handle different data types
        if (attr.data_type === 'Boolean') {
          // For boolean, only update if checkbox is checked
          if (value === true) {
            updates[attr.name] = true
          }
        } else {
          // For other types, only update if value is provided and not empty
          if (value !== undefined && value !== '') {
            updates[attr.name] = value
          }
        }
      })

      if (Object.keys(updates).length === 0) {
        throw new Error('No updates provided. Please fill at least one field.')
      }

      return dataInputsService.bulkUpdateRecords(
        dataInput.id,
        selectedRecords.map(r => r.id),
        updates
      )
    },
    onSuccess: (result) => {
      setOperationResult(result)
      if (result.success_count > 0) {
        // Invalidate records query to refresh the list
        queryClient.invalidateQueries(['records', dataInput.id])
        toast.success(`Successfully updated ${result.success_count} records`)
        reset() // Clear form data after successful operation
        setFileUploads({}) // Clear file uploads
        setUploadingFiles({}) // Clear upload progress
        if (onSuccess) onSuccess()
      }
      if (result.error_count > 0) {
        toast.error(`${result.error_count} records failed to update`)
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update records')
    }
  })

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      return dataInputsService.bulkDeleteRecords(
        dataInput.id,
        selectedRecords.map(r => r.id)
      )
    },
    onSuccess: (result) => {
      setOperationResult(result)
      if (result.success_count > 0) {
        // Invalidate records query to refresh the list
        queryClient.invalidateQueries(['records', dataInput.id])
        toast.success(`Successfully deleted ${result.success_count} records`)
        if (onSuccess) onSuccess()
      }
      if (result.error_count > 0) {
        toast.error(`${result.error_count} records failed to delete`)
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete records')
    }
  })

  const handleOperationSubmit = (data) => {
    if (operation === 'update') {
      bulkUpdateMutation.mutate(data)
    } else if (operation === 'delete') {
      setShowConfirmation(true)
    }
  }

  const confirmDelete = () => {
    bulkDeleteMutation.mutate()
    setShowConfirmation(false)
  }

  const handleClose = () => {
    reset()
    setOperation('update')
    setShowConfirmation(false)
    setOperationResult(null)
    setFileUploads({}) // Clear file uploads
    setUploadingFiles({}) // Clear upload progress
    onClose()
  }

  // Clear operation result and form data when modal opens
  useEffect(() => {
    if (isOpen) {
      setOperationResult(null)
      setShowConfirmation(false)
      setFileUploads({}) // Clear file uploads
      setUploadingFiles({}) // Clear upload progress
      reset() // Clear form data
    }
  }, [isOpen, reset])

  if (!isOpen) return null

  const isLoading = bulkUpdateMutation.isLoading || bulkDeleteMutation.isLoading

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={handleClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Header */}
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
              {operation === 'update' ? (
                <Edit className="h-6 w-6 text-blue-600" />
              ) : (
                <Trash2 className="h-6 w-6 text-red-600" />
              )}
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Bulk Operations
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {selectedRecords.length} record{selectedRecords.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            </div>
          </div>

          {/* Operation Selection */}
          {!showConfirmation && !operationResult && (
            <div className="mt-6">
              <div className="flex space-x-4 mb-6">
                <button
                  type="button"
                  onClick={() => setOperation('update')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
                    operation === 'update'
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                      : 'bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  <Edit className="h-4 w-4 inline mr-2" />
                  Bulk Update
                </button>
                <button
                  type="button"
                  onClick={() => setOperation('delete')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
                    operation === 'delete'
                      ? 'bg-red-100 text-red-700 border-2 border-red-300'
                      : 'bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  <Trash2 className="h-4 w-4 inline mr-2" />
                  Bulk Delete
                </button>
              </div>

              {/* Update Form */}
              {operation === 'update' && (
                <form onSubmit={handleSubmit(handleOperationSubmit)}>
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <div className="flex">
                        <AlertTriangle className="h-5 w-5 text-blue-400 mr-2" />
                        <div className="text-sm text-blue-700">
                          <div className="font-medium mb-1">Bulk Update Guidelines:</div>
                          <ul className="text-xs space-y-1">
                            <li>• Only non-unique attributes can be updated in bulk</li>
                            <li>• Leave fields empty to keep existing values unchanged</li>
                            <li>• Only filled fields will be updated across selected records</li>
                            <li>• For file fields, upload a new file to replace existing files in all selected records</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {nonUniqueAttributes.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        No non-unique attributes available for bulk update.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {nonUniqueAttributes.map((attribute) => (
                          <div key={attribute.id}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {attribute.label || attribute.name}
                              <span className="text-xs text-gray-500 ml-1 font-normal">
                                ({attribute.data_type})
                              </span>
                            </label>
                            {renderField(attribute)}
                            {errors[attribute.name] && (
                              <p className="mt-1 text-xs text-red-600">{errors[attribute.name].message}</p>
                            )}
                            {attribute.data_type === 'Phone Number' && (
                              <p className="mt-1 text-xs text-gray-500">
                                Must start with 0, minimum 8 digits. Leave empty to skip this field.
                              </p>
                            )}
                            {attribute.help_text && (
                              <p className="mt-1 text-xs text-gray-500">{attribute.help_text}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {nonUniqueAttributes.length > 0 && (
                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Updating...' : `Update ${selectedRecords.length} Records`}
                      </button>
                    </div>
                  )}
                </form>
              )}

              {/* Delete Confirmation */}
              {operation === 'delete' && (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex">
                      <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                      <div className="text-sm text-red-700">
                        This action will permanently delete {selectedRecords.length} record{selectedRecords.length !== 1 ? 's' : ''}. This cannot be undone.
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmDelete}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Deleting...' : `Delete ${selectedRecords.length} Records`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Operation Result */}
          {operationResult && (
            <div className="mt-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  {operationResult.success_count > 0 && (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-5 w-5 mr-1" />
                      <span className="text-sm font-medium">
                        {operationResult.success_count} successful
                      </span>
                    </div>
                  )}
                  {operationResult.error_count > 0 && (
                    <div className="flex items-center text-red-600">
                      <XCircle className="h-5 w-5 mr-1" />
                      <span className="text-sm font-medium">
                        {operationResult.error_count} failed
                      </span>
                    </div>
                  )}
                </div>

                {operationResult.errors && operationResult.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Errors:</h4>
                    <div className="space-y-1">
                      {operationResult.errors.map((error, index) => (
                        <div key={index} className="text-xs text-red-700">
                          Record ID {error.record_id}: {error.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BulkOperationsModal